import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Megaphone, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

interface Announcement {
  id: number;
  message: string;
  level: "info" | "warn" | string;
  is_active: boolean;
  created_at: string;
}

export function AnnouncementsPage() {
  const qc = useQueryClient();
  const [message, setMessage] = useState("");
  const [level, setLevel] = useState<"info" | "warn">("info");

  const { data, isLoading } = useQuery({
    queryKey: ["announcements"],
    queryFn: () => api.get<{ items: Announcement[] }>("/announcements").then((r) => r.items),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["announcements"] });

  const createM = useMutation({
    mutationFn: () => api.post("/announcements", { message: message.trim(), level, is_active: true }),
    onSuccess: () => {
      setMessage("");
      setLevel("info");
      invalidate();
    },
  });
  const toggleM = useMutation({
    mutationFn: (a: Announcement) => api.patch(`/announcements/${a.id}`, { is_active: !a.is_active }),
    onSuccess: invalidate,
  });
  const deleteM = useMutation({
    mutationFn: (id: number) => api.delete(`/announcements/${id}`),
    onSuccess: invalidate,
  });

  const items = data ?? [];
  const activeCount = items.filter((a) => a.is_active).length;

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">공지 관리</h1>
        <p className="text-xs text-muted-foreground">
          사용자 웹(arkiveai) 상단 배너에 노출됩니다. 활성 공지 중 <b>가장 최근 1건</b>이 표시됩니다.
        </p>
      </header>

      {/* 등록 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="h-4 w-4" /> 새 공지 등록
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={2}
            placeholder="예) 1/30(목) 02:00~04:00 시스템 점검 예정입니다."
            className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">유형</label>
            <Select value={level} onChange={(e) => setLevel(e.target.value as "info" | "warn")}>
              <option value="info">일반(info)</option>
              <option value="warn">주의(warn — 강조)</option>
            </Select>
            <Button
              className="ml-auto"
              disabled={!message.trim() || createM.isPending}
              onClick={() => createM.mutate()}
            >
              등록
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 목록 */}
      <Card>
        <CardHeader>
          <CardTitle>공지 목록 {activeCount > 0 && `(활성 ${activeCount})`}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-sm text-muted-foreground">등록된 공지가 없습니다.</div>
          ) : (
            <ul className="divide-y">
              {items.map((a) => (
                <li key={a.id} className="flex items-start gap-3 py-3">
                  <span
                    className={
                      "mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold " +
                      (a.level === "warn"
                        ? "bg-orange-100 text-orange-700"
                        : "bg-blue-100 text-blue-700")
                    }
                  >
                    {a.level === "warn" ? "주의" : "일반"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className={"whitespace-pre-wrap break-words text-sm " + (a.is_active ? "" : "text-muted-foreground line-through")}>
                      {a.message}
                    </p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {a.created_at?.slice(0, 16).replace("T", " ")}
                    </p>
                  </div>
                  <label className="flex shrink-0 cursor-pointer items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={a.is_active}
                      onChange={() => toggleM.mutate(a)}
                      className="h-4 w-4"
                    />
                    {a.is_active ? "켜짐" : "꺼짐"}
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (window.confirm("이 공지를 삭제할까요?")) deleteM.mutate(a.id);
                    }}
                    aria-label="삭제"
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
